import { useEffect, useState } from "react";
import { Quote } from "lucide-react";

const QUOTE = "We see content not just as media — but as a structured, scalable product, powered by systems, data, and technology.";

function FounderSilhouette() {
  return (
    <svg
      viewBox="0 0 240 240"
      role="img"
      aria-label="Founder"
      className="h-44 w-44 md:h-56 md:w-56"
    >
      {/* Concentric backdrop rings */}
      <circle cx="120" cy="120" r="110" fill="none" stroke="#E5E5E5" strokeWidth="1" />
      <circle cx="120" cy="120" r="86" fill="none" stroke="#E5E5E5" strokeWidth="1" />
      <circle cx="120" cy="120" r="62" fill="#FAFAFA" />
      {/* Silhouette */}
      <g fill="#0A0A0A">
        {/* head */}
        <circle cx="120" cy="98" r="26" />
        {/* shoulders */}
        <path d="M62 178 C 62 152, 88 134, 120 134 C 152 134, 178 152, 178 178 L 178 196 L 62 196 Z" />
      </g>
      {/* small AI node accent */}
      <circle cx="186" cy="60" r="6" fill="#0A0A0A" />
      <circle cx="186" cy="60" r="11" fill="none" stroke="#0A0A0A" strokeWidth="1" />
      <circle cx="186" cy="60" r="17" fill="none" stroke="#0A0A0A" strokeOpacity="0.3" strokeWidth="1" />
    </svg>
  );
}

function TypedText({ text, speed = 22 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (count >= text.length) return;
    const id = setTimeout(() => setCount((c) => c + 1), speed);
    return () => clearTimeout(id);
  }, [count, text.length, speed]);

  return (
    <span>
      {text.slice(0, count)}
      <span
        className="ct-cursor ml-0.5 inline-block h-[0.9em] w-[2px] translate-y-[2px] bg-[#0A0A0A]"
        aria-hidden="true"
      />
    </span>
  );
}

export default function Founder() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="ct-container scroll-mt-28 py-24 md:py-32"
    >
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="ct-eyebrow">About the Founder</span>
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-5xl">
            Built by an operator,
            <br />
            <span className="text-[#737373]">for learners.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">
            A practitioner's lens on enterprise tech, digital transformation, and
            IT infrastructure — applied to a domain that touches everyone: learning.
          </p>

          <div className="mt-10 hidden lg:flex">
            <FounderSilhouette />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative rounded-3xl border border-slate-200 bg-white p-8 md:p-10">
            <Quote className="absolute right-7 top-7 h-9 w-9 text-neutral-200" />
            <p className="font-display text-xl leading-snug text-[#0F172A] md:text-2xl">
              <TypedText text={QUOTE} />
            </p>

            <div className="mt-9 grid gap-7 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-neutral-50 p-6">
                <h3 className="font-display text-base font-semibold text-[#0A0A0A]">
                  Enterprise depth
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Extensive experience across complex systems, large-scale
                  environments, digital transformation and IT infrastructure
                  management.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-neutral-50 p-6">
                <h3 className="font-display text-base font-semibold text-[#0A0A0A]">
                  Outcome-driven
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  A practical understanding of both technology and user behavior —
                  focused on building solutions that are simple, effective, and
                  accessible.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-neutral-50 p-6">
                <h3 className="font-display text-base font-semibold text-[#0A0A0A]">
                  Eduzy as the proof
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Through Eduzy — our flagship initiative — we simplify learning
                  while keeping it affordable and effective for a wider audience.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-neutral-50 p-6">
                <h3 className="font-display text-base font-semibold text-[#0A0A0A]">
                  India × Global
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Built in India with a global mindset — creating digital experiences
                  that empower learners, creators, and communities worldwide.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex justify-center lg:hidden">
            <FounderSilhouette />
          </div>
        </div>
      </div>
    </section>
  );
}
